import { MagicLayout } from '@magic/layout';
import { PaylineController } from 'controllers/stateControllers/Payline.controller';
import type { PopupController, PopupText } from 'controllers/Popup.controller';
import type { ReelController } from 'controllers/stateControllers/Reel.controller';
import { ParseAmount } from 'stake-engine';
import type {
    RoundData,
    RoundState,
    SymbolCell,
    WinLine,
    WinPosition,
} from '../../utils/api.types';
import {
    getBetAmount,
    isFreeSpinsActive,
    setFreeSpinsActive,
    setGameState,
} from '../../stores/api.store';
import { wait } from '@magic/utils';
import { getNextStateData } from '../../utils/state.utils';
import { winToPayout } from '../../utils/win.utils';
import { settingsStore } from '../../stores/settings.store';
import {
    clearFreeSpinsProgress,
    getFreeSpinsProgress,
    saveFreeSpinsProgress,
} from '../../utils/freeSpinsStorage';

// TODO: review thsi whole controllers system to be universal, simple and easily extendable for different game states and features (free spins, bonus games, etc). Maybe we can have some BaseStateController with common methods and then extend it for each state type?

/**
 * How often the win presentation replays while a result is on screen. Matches
 * the payline export's `show`, so a line redraws exactly as the last pass ends.
 */
const winLoopInterval = 2000;

export class RoundController {
    private paylines: PaylineController;

    /**
     * Whether the free spins feature is running. Kept in the store rather than
     * on the controller so the API layer can pick the play mode (FREESPINS vs
     * BASE) without reaching into the round controller.
     */
    get freeSpinsActive(): boolean {
        return isFreeSpinsActive();
    }

    set freeSpinsActive(active: boolean) {
        setFreeSpinsActive(active);
    }

    /** betID of the round currently playing — tags persisted free spins progress. */
    private currentBetID = 0;
    /** last known free spins total — a retrigger only reports the new total,
     *  so the popup needs this to show how many spins were just added */
    private freeSpinsTotal = 0;

    /** The round being played has multiple reveals (free spins / bonus). */
    private multiRevealRound = false;

    /**
     * The bet the round being played was placed at, in raw RGS units. The running
     * wins inside its book are multipliers of it, so it is what turns those into
     * payouts. Read off the round rather than the store: the player can move the
     * bet while the sequence plays, and this round still pays what it was staked at.
     */
    private roundBet = 0;

    /**
     * What the round being played pays in total, in raw RGS units — reported by
     * the RGS on the round itself, so the closing total is never derived from the
     * book. It is also the amount EndRound credits, which is what the player sees
     * the balance move by.
     */
    private roundPayout = 0;

    /** Index of the book's closing `setTotalWin` — the one showing the round total. */
    private lastTotalWinIndex = -1;

    /** Last balance shown, to pick the up/down animation. Undefined until the first one lands. */
    private balance?: number;

    /**
     * Replays the win presentation on screen every `winLoopInterval`. Set while
     * a result is showing and cleared when the board is handed to the next spin.
     */
    private winLoop?: ReturnType<typeof setInterval>;

    constructor(
        private spine: MagicLayout,
        private reelController: ReelController,
        /** The game's shared popup — the same one the buy bonus offer opens. */
        private popup: PopupController,
        /** Confirms a played book event index to the backend (fire-and-forget). */
        private recordEvent: (index: number) => void,
    ) {
        this.paylines = new PaylineController(this.spine, this.reelController);

        this.spine.texts.set('freeSpinsProgress', ``);
    }

    async playState(round: RoundData) {
        this.currentBetID = round.betID;
        // a restored round may come back without its bet — fall back to the
        // current one rather than resolving every win in the book to 0
        this.roundBet = round.amount || getBetAmount();
        this.roundPayout = round.payout ?? 0;
        const states = (round.state ?? []) as RoundState[];

        // the running totals inside the book are multipliers, but the last one is
        // the round total — and that one the RGS reports outright as `payout`
        this.lastTotalWinIndex = states.reduce(
            (last, state, i) => (state.type === 'setTotalWin' ? i : last),
            -1,
        );

        // multi-reveal books (free spins / bonus) report played reveals to the
        // backend so it can track progress through the round (see the reveal case)
        this.multiRevealRound = states.filter((state) => state.type === 'reveal').length > 1;

        // on a reload the backend replays the still-active round from the start;
        // if we have saved progress for it, skip ahead to the free spin the
        // player was on instead of replaying the feature from the trigger
        const startIndex = await this.resolveFreeSpinsResume(round.betID, states);

        for (let index = startIndex; index < states.length; index++) {
            await this.playQueueState(states[index], index);
        }
    }

    /**
     * Decides where to start playing a round. Returns 0 for a normal round, or
     * the index of the current free spin when resuming a reloaded round: it
     * restores the free spins UI and waits for the player to dismiss a "spins
     * left" popup before the round loop continues from that spin.
     */
    private async resolveFreeSpinsResume(betID: number, states: RoundState[]): Promise<number> {
        const progress = getFreeSpinsProgress();

        if (!progress) return 0;

        // progress tagged with another round is stale — drop it and start fresh
        if (progress.betID !== betID) {
            clearFreeSpinsProgress();
            return 0;
        }

        const resumeState = states[progress.resumeIndex];

        if (resumeState?.type !== 'updateFreeSpin') {
            clearFreeSpinsProgress();
            return 0;
        }

        await this.resumeFreeSpins(resumeState);

        return progress.resumeIndex;
    }

    /**
     * Restores the free spins presentation after a reload and holds until the
     * player acknowledges how many spins are left; the round loop then replays
     * the spin they were on and continues to the end of the feature.
     */
    private async resumeFreeSpins(resumeState: Extract<RoundState, { type: 'updateFreeSpin' }>) {
        this.freeSpinsActive = true;

        // re-enter free spins mode: the trigger normally hides the spin button
        // (state_freeSpinTrigger -> hide_spin_button), but it's skipped on
        // resume, so replay it here or the button stays visible until freeSpinEnd.
        // Awaited so it finishes before the popup: while it counts as active it
        // pushes show_popup to a higher track, and the close-click hide_popup
        // would later land on a lower track, masked by show_popup's end pose.
        await this.spine.animations.playState('freeSpinTrigger');

        // give the reels a board behind the popup instead of the empty default pose
        this.reelController.fillBoardWithRandomSymbols();

        // the spin the player is on, counting the one about to be replayed
        const progressText = `${resumeState.amount + 1} OF ${resumeState.total}`;
        this.spine.texts.set('freeSpinsProgress', progressText);

        await this.showPopup({
            title: `free spins left`,
            content: progressText,
            buttonText: `continue`,
        });
    }

    private async playQueueState(state: RoundState, index: number) {
        if (settingsStore.getState().roundDebug) {
            console.groupCollapsed(`🧟 state`, state?.type);
            console.table(state);
            console.groupEnd();
        }

        this.spine.animations.playState(state?.type);

        switch (state?.type) {
            case 'reveal': // 1 (after aech spin)
                // a free spin is not preceded by a player spin, so this is where
                // the previous spin's win presentation comes off the board
                this.endWinPresentation();

                // confirm progress through multi-reveal books to the backend
                // (mirrors the official web-sdk: it records every reveal of a
                // bonus book via /bet/event so the RGS can resume/settle it)
                if (this.multiRevealRound) {
                    this.recordEvent(state.index);
                }

                // free spin rounds arrive without a player-initiated spin —
                // start the reels (no-op when already spinning) so each free
                // spin plays a real spin instead of swapping the board in place
                if (state.gameType === 'freegame') {
                    this.spine.animations.playByName('blur', true);
                    this.reelController.spin();
                }

                // reelController.reveal applies the board instantly when the
                // reels are not spinning (initial reveal of a restored round)
                await this.revealResult(state.board, state.anticipation);

                if (state.gameType === 'freegame') {
                    this.freeSpinsActive = true;
                }
                // all symbols results are set
                break;

            case 'winInfo': // 2 (only if user wins something)
                this.onWinInfo(state);
                break;

            case 'setWin': // 4 (only if user wins something)
                if (state.winLevel > 0) {
                    switch (true) {
                        case state.winLevel > 5:
                            this.spine.animations.playState('bigWin');
                            break;
                        case state.winLevel > 7:
                            this.spine.animations.playState('megaWin');
                            break;
                        case state.winLevel > 9:
                            this.spine.animations.playState('epicWin');
                            break;
                    }
                }

                await this.setWinAmountText(state.amount);

                await this.spine.animations.stopState('setWin');

                break;

            case 'wincap': // 3 (only if user got big win)
                this.spine.animations.playState('bigWin');

                await this.showPopup({
                    title: `big win`,
                    content: this.formatWin(state.amount),
                    buttonText: `collect`,
                });
                break;

            case 'setTotalWin': // 5 (after each spin)
                // the closing total is what the round actually pays, so it comes
                // straight off the round; only the totals running up to it have
                // to be resolved from the book's multipliers
                await (index === this.lastTotalWinIndex
                    ? this.setRoundPayout()
                    : this.setTotalWin(state.amount));

                if (Number(state.amount) > 0 || this.freeSpinsActive) {
                    await wait(1000);
                }
                break;

            case 'finalWin':
                // this.setTotalWin(state.amount);
                setGameState('idle');

                // the round is still open here — Main.controller settles it once
                // this sequence returns, which is what credits the payout and
                // counts the balance up while the win is on screen

                if (Number(state.amount) > 0 || this.freeSpinsActive) {
                    await wait(1000);
                }
                break;

            // FreeSpins -----------------------------

            case 'freeSpinTrigger':
                this.freeSpinsTotal = state.totalFs;
                await this.showFreeSpinsTriggerSymbols(state.positions);

                wait(300).then(() => {
                    this.spine.texts.set('popup_content', String(state.totalFs), true);
                });

                await this.showPopup({
                    title: `free spins`,
                    content: `0`,
                    buttonText: `play`,
                });
                break;

            case 'freeSpinRetrigger': {
                // show only the spins this retrigger added, not the new total
                const addedFs = state.totalFs - this.freeSpinsTotal;
                this.freeSpinsTotal = state.totalFs;

                await this.showFreeSpinsTriggerSymbols(state.positions);

                wait(300).then(() => {
                    this.spine.texts.set('popup_content', String(addedFs), true);
                });

                await this.showPopup({
                    title: `More free spins!!!`,
                    buttonText: `play`,
                });
                break;
            }

            case 'updateFreeSpin': // on every free spin started
                this.freeSpinsTotal = state.total;
                this.spine.texts.set('freeSpinsProgress', `${state.amount + 1} OF ${state.total}`);
                // remember the spin we're on so a reload can resume from here
                saveFreeSpinsProgress({ betID: this.currentBetID, resumeIndex: index });
                break;

            case 'freeSpinEnd':
                // the round is NOT settled here: it stays open — with the saved
                // progress pointing at the last free spin — until the whole
                // sequence has played out, so a refresh on the feature win popup
                // resumes the round from that spin instead of losing it.
                // Main.controller settles it (and drops the progress) once this
                // popup has been dismissed and the book has finished.

                wait(300).then(() => {
                    this.spine.texts.set('popup_content', this.formatWin(state.amount), true);
                });

                // if (state.winLevel > 0) {
                // TODO: show big win animation if win level is big enough
                // }

                await this.showPopup({
                    title: `free spins win`,
                    content: `0`,
                    buttonText: `OK`,
                });

                this.freeSpinsActive = false;
                break;

            default:
                console.warn(`Unhandled state type: ${state.type}`);
        }
    }

    private async onWinInfo(state: Extract<RoundState, { type: 'winInfo' }>) {
        const nextSetWin = getNextStateData(state, 'setWin');

        // a big win takes over the plain win text (the following setWin state
        // plays its own presentation), but the paylines and the win symbol
        // animations always show
        if (!nextSetWin || !(nextSetWin.winLevel > 0)) {
            this.spine.texts.set('win', this.formatWin(state.totalWin), true);
            this.spine.animations.playState('winInfo');
        }

        // the presentation stays up from here and replays on its own — it is the
        // next spin that takes it down, not a timeout. The loop is started before
        // the first pass rather than after it, so every replay lands one interval
        // apart instead of the first one being a pass late.
        this.startWinLoop(state.wins);
        await this.showWins(state.wins);
    }

    /**
     * Shows what a reveal won: the lines it paid on and the symbols that made
     * them, with every other visible symbol dimmed. The payline instances stay
     * on the board afterwards, so the loop can redraw them without rebuilding.
     */
    private async showWins(wins: WinLine[]) {
        await Promise.all([this.paylines.show(wins), this.activateWinSymbols(wins)]);
    }

    /** One more pass of the presentation already on the board. */
    private async replayWins(wins: WinLine[]) {
        await Promise.all([this.paylines.replay(), this.activateWinSymbols(wins)]);
    }

    /**
     * Keeps a result on screen, replaying the lines and their symbols together
     * every `winLoopInterval` so the two stay in step however long each runs.
     */
    private startWinLoop(wins: WinLine[]) {
        this.stopWinLoop();

        if (!wins.length) return;

        this.winLoop = setInterval(() => void this.replayWins(wins), winLoopInterval);
    }

    private stopWinLoop() {
        if (this.winLoop === undefined) return;

        clearInterval(this.winLoop);
        this.winLoop = undefined;
    }

    /**
     * Clears the board of the last result: the loop stops, the payline instances
     * are destroyed, and the symbols drop back to idle. Called before anything
     * that takes the board over — a spin, or the free spins trigger showing its
     * own symbols — so nothing from the previous win survives into it.
     */
    private endWinPresentation() {
        this.stopWinLoop();
        this.paylines.clear();

        // the dimmed (inactive) symbols keep their swapped attachment otherwise,
        // so the board has to be put back before the next state poses it
        this.spine.animations.playByName('idle', true);
    }

    /**
     * Holds the round until the player acknowledges what just happened.
     *
     * Every popup the round opens is a report — a trigger, a retrigger, a win — so
     * they all show the accept button alone: there is nothing here to decline, and a
     * close X would leave the sequence waiting on a choice it has no branch for.
     */
    private async showPopup(text?: PopupText): Promise<void> {
        await this.popup.show({ ...text, layout: 'only_accept' });
    }

    private async revealResult(board: SymbolCell[][], anticipation?: number[]) {
        // the reels fill with the result symbols and stop one by one;
        // when this resolves the visible board is final
        await this.reelController.reveal(board, anticipation);

        // solo, so the symbols drop their blur state entirely instead of
        // stacking idle on another track above it
        this.spine.animations.playByName('idle', true);
    }

    private async activateWinSymbols(wins: WinLine[]) {
        await this.activateSymbols(wins.flatMap((win) => win.positions));
    }

    private async showFreeSpinsTriggerSymbols(positions: WinPosition[]) {
        // the trigger highlights the scatters instead, and the win loop would
        // otherwise keep re-posing the symbols underneath it every interval
        this.endWinPresentation();

        await this.activateSymbols(positions);
    }

    /**
     * Plays `active` on the symbols at the given board positions and
     * `inactive` on every other visible symbol. Positions outside the
     * visible window are dropped — hidden buffer symbols never animate.
     *
     * The symbol spines show exactly one state at a time (idle/blur/
     * active/inactive), so the animations play solo: stacking them on
     * extra tracks leaves stale entries that re-apply their attachment
     * over later states (e.g. inactive gravestones during the next spin).
     */
    private async activateSymbols(positions: WinPosition[]) {
        const activeIDs = new Set(
            positions.map(({ reel, row }) => this.getSymbolIDByPosition(reel, row)).filter(Boolean),
        );

        if (!activeIDs.size) return;

        await Promise.all(
            this.reelController
                .getVisibleSymbolIDs()
                .map((symbolID) =>
                    this.spine.animations.play(
                        symbolID,
                        activeIDs.has(symbolID) ? 'active' : 'inactive',
                        true,
                    ),
                ),
        );
    }

    private getSymbolIDByPosition(reel: number, row: number) {
        return this.reelController.getSymbolIDByPosition(reel, row);
    }

    setBet(betAmount?: number) {
        this.spine.texts.set('bet', `${betAmount ? betAmount / 1000000 : ''}`, true);
    }

    setBalance(balance: { amount: number; currency: string }) {
        // the displayed amount is rounded, so a change too small to show (or the
        // first balance there is) must not play a direction animation
        const previous = this.balance;
        this.balance = balance.amount;

        if (previous !== undefined && previous.toFixed(2) !== balance.amount.toFixed(2)) {
            this.spine.animations.playState(
                previous < balance.amount ? 'balance_up' : 'balance_down',
            );
        }

        this.spine.texts.set(
            'balance',
            `${balance.amount.toFixed(2)} ${balance.currency.toUpperCase()}`,
            true,
        );
    }

    async setTotalWin(win: number | string | undefined, level = 0) {
        if (Number(win) === 0) {
            await this.spine.texts.set('total_win', `-`);
            return;
        }

        await this.setTotalWinText(win !== undefined ? this.formatWin(win) : '-', level);
    }

    /**
     * Shows what the round pays as the RGS reports it, rather than a total added
     * up from the book — this is the figure EndRound credits, so it is the one
     * that has to match the balance the player is left with.
     */
    private async setRoundPayout(level = 0) {
        if (!this.roundPayout) {
            await this.spine.texts.set('total_win', `-`);
            return;
        }

        await this.setTotalWinText(ParseAmount(this.roundPayout).toFixed(2), level);
    }

    private async setTotalWinText(text: string, level: number) {
        const duration = level < 3 ? 0 : level < 6 ? 3000 : 5000;

        await this.spine.texts.set('total_win', text, true, duration);
    }

    private async setWinAmountText(win: number | string | undefined) {
        if (win === 0) {
            await this.spine.texts.set('win', ``, true);
            return;
        }

        await this.spine.texts.set('win', win !== undefined ? this.formatWin(win) : '', true);
    }

    /**
     * A book win amount as the payout it pays at this round's bet. Non-numeric
     * values (the `-` placeholder, undefined) pass through untouched.
     */
    private formatWin(win: number | string | undefined): string {
        const amount = Number(win);

        if (!Number.isFinite(amount)) return `${win ?? ''}`;

        return ParseAmount(winToPayout(amount, this.roundBet)).toFixed(2);
    }

    onSpinStart() {
        // the last result stays on the board until the player spins again
        this.endWinPresentation();

        if (!this.freeSpinsActive) {
            this.spine.texts.set('freeSpinsProgress', ``); // reset free spins progress on each spin start
        }
    }
}
